import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { TextInput } from "@msuite/ui/form";
import { useSearchForm } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { type CrewDefinitionListItem, useCrewDefinitions } from "@/hooks/use-crew-definitions";
import { spanReachesWindow, useCapacityCalendar } from "../capacity-calendar";
import { ManagerSlotCard } from "./manager-slot-card";

/** One window the reader holds, carrying the crew it is on so the card can name it. */
interface OwnManagerSlot {
	crewDefinition: CrewDefinitionListItem;
	managerSlot: CrewDefinitionListItem["_managerSlots"][number];
}

/**
 * The crews segment of the resources panel as the manager view shows it: the windows the reader
 * holds, and nothing else.
 *
 * The planner reads this panel to see every crew there is; a manager reads it to see the crews
 * that are theirs to schedule, which is a different and much shorter list. Both are derived from
 * the same `useCrewDefinitions` read — every crew carries its manager slots — so the two segments
 * never disagree about a span.
 *
 * **It filters on the slot's window, not the crew's.** A crew standing all year whose window over
 * it ended last month is not one this reader can plan in these weeks, and showing it would put
 * back exactly the noise this list exists to remove.
 */
export function ManagerSlotResources() {
	/** Hooks */
	const { t } = useTranslation();
	const searchForm = useSearchForm("capacity-manager-slots-resources");
	const controller = useCapacityCalendar();
	const { user } = useAuth();

	/** Query */
	const { crewDefinitions, isLoading } = useCrewDefinitions();

	/** Computed */
	const ownSlots = useMemo(() => {
		const result: OwnManagerSlot[] = [];
		if (!user?.id) return result;
		for (const crewDefinition of crewDefinitions) {
			for (const managerSlot of crewDefinition._managerSlots) {
				if (managerSlot.userId !== user.id) continue;
				if (!spanReachesWindow(managerSlot, controller.windowStart, controller.windowEnd)) {
					continue;
				}
				result.push({ crewDefinition, managerSlot });
			}
		}
		result.sort((a, b) => a.managerSlot.startDate.localeCompare(b.managerSlot.startDate));
		return result;
	}, [crewDefinitions, user?.id, controller.windowStart, controller.windowEnd]);

	// The search reads the crew's name and only that. `useFilter` walks every nested field, which
	// over a crew reaches its ids, its members and its other managers' slots — matches the reader
	// cannot see the cause of. The crew is what a card is identified by, so the name is the field.
	const filtered = useMemo(() => {
		const search = searchForm.searchValue?.toLowerCase().trim();
		if (!search) return ownSlots;
		const result: OwnManagerSlot[] = [];
		for (const item of ownSlots) {
			if (item.crewDefinition.displayName?.toLowerCase().includes(search)) result.push(item);
		}
		return result;
	}, [ownSlots, searchForm.searchValue]);

	/** Render */
	return (
		<ContentCard
			title={t("planning.crews.my_manager_slots")}
			icon={icon("UserRoundCog")}
			contentClassName="v-stack flex-1 min-h-0 overflow-y-auto"
			className="h-full overflow-hidden"
			variant="plain"
		>
			<div className="h-stack p-1">
				<div {...searchForm.getSearchInputWrapperProps()}>
					<TextInput {...searchForm.getSearchInputProps()} />
				</div>
			</div>
			{isLoading && <BlankSlate isLoading />}
			{!isLoading && !filtered.length && (
				<BlankSlate
					withBorder
					label={t("planning.crews.no_manager_slots_in_window")}
				/>
			)}
			{filtered.map((item) => (
				<ManagerSlotCard
					key={item.managerSlot.id}
					crewDefinition={item.crewDefinition}
					managerSlot={item.managerSlot}
				/>
			))}
		</ContentCard>
	);
}
